import React, { useState, useEffect } from 'react'
import { Switch, Spin } from 'antd'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'
import mailboxApiHelper from 'services/mailbox-api'
import moment from 'moment'
import { confirmBase } from 'utils/ui'
import AutomaticReplyEditor from './AutomaticReplyEditor'
import stylesEditor from './AutomaticReplyEditor/style.module.scss'
import { Loading3QuartersOutlined } from '@ant-design/icons'

interface AutomaticReply {
  enabled: boolean
  since?: string
  until?: string
  subject?: string
  message?: string
  ext_enabled?: boolean
  ext_subject?: string
  ext_message?: string
}

interface AutomaticRepliesPanelProps {
  active: boolean
}

interface EditorRef {
  getData: () => any
  setIsLoading: (loading: boolean) => void
}

const AutomaticRepliesPanel: React.FC<AutomaticRepliesPanelProps> = ({ active }) => {
  const [isEnabled, setIsEnabled] = useState(false)
  const [isEmpty, setIsEmpty] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [automaticReplies, setAutomaticReplies] = useState<AutomaticReply>({} as AutomaticReply)
  const { t } = useTranslation()

  const editorRef = React.createRef<EditorRef>()

  useEffect(() => {
    if (!active) {
      return
    }

    const fetchAutomaticReplies = async (): Promise<void> => {
      setIsLoading(true)
      const res = await mailboxApiHelper.getAutomaticReplies()
      console.log(res)
      if (Object.keys(res).length === 0) {
        setIsEmpty(true)
        setIsEnabled(false)
      } else {
        setIsEmpty(false)
        setIsEnabled(res.enabled)
        setAutomaticReplies(res as AutomaticReply)
      }
      setIsLoading(false)
    }
    fetchAutomaticReplies()
  }, [active])

  const onSave = async (data: any): Promise<void> => {
    console.log('onSave', data)

    const sendData = {
      enabled: data.enabled,
      since: data.since,
      until: data.until,
      subject: data.subject || '',
      message: data.message || '',
      ext_enabled: true,
      ext_subject: data.subject || '',
      ext_message: data.message || '',
    }

    let result = null
    setIsLoading(true)
    editorRef.current?.setIsLoading(true)
    try {
      if (isEmpty) {
        result = await mailboxApiHelper.createAutomaticReplies(sendData)
        console.log('createAutomaticReplies', result)
      } else {
        result = await mailboxApiHelper.updateAutomaticReplies(sendData)
        console.log('updateAutomaticReplies', result)
      }
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
      editorRef.current?.setIsLoading(false)
    }
    if (result) {
      setAutomaticReplies(result)
      setIsEnabled(result.enabled)
    }
  }

  const handleManageClick = (enabled: boolean = false): void => {
    console.log('manage')

    const autoReplyData = automaticReplies
    if (enabled) {
      autoReplyData.enabled = true
    }

    const modalObj = confirmBase({
      title: t('settings.accountAutomaticRepliesTab.editorTitle'),
      closable: false,
      content: (
        <AutomaticReplyEditor
          ref={editorRef}
          data={autoReplyData}
          onSave={() => {
            const editorData = editorRef.current!.getData()
            console.log('editor data:', editorData)
            onSave(editorData)
              .then(() => {
                modalObj.destroy()
              })
              .catch((error: Error) => {
                alert(error.message)
              })
          }}
          onCancel={() => {
            if (isEmpty) {
              setIsEnabled(false)
              if (automaticReplies.enabled) {
                setAutomaticReplies({ ...automaticReplies, enabled: false })
              }
            }
            modalObj.destroy()
          }}
        />
      ),
      width: 640,
      wrapClassName: stylesEditor.editorModal,
      footer: null, // MUST BE NULL
    })
  }

  const handleSwitchChange = async (checked: boolean): Promise<void> => {
    setIsEnabled(checked)
    if (isEmpty) {
      if (checked) {
        handleManageClick(true)
      }
    } else {
      const data = { ...automaticReplies }
      data.enabled = checked
      onSave(data)
    }
  }

  const getDescription = (): string | null => {
    if (!automaticReplies.enabled) {
      return null
    }
    if (automaticReplies.since && automaticReplies.until) {
      const since = moment(automaticReplies.since).format('YYYY/MM/DD HH:mm')
      const until = moment(automaticReplies.until).format('YYYY/MM/DD HH:mm')
      return `${since} - ${until}`
    } else if (automaticReplies.since) {
      const since = moment(automaticReplies.since).format('YYYY/MM/DD HH:mm')
      return `${since} - ${t('settings.accountAutomaticRepliesTab.indefinitely')}`
    }
    return null
  }

  return (
    <div className={styles.automaticRepliesPanel}>
      <Spin indicator={<Loading3QuartersOutlined spin />} spinning={isLoading}>
        <div className={styles.content}>
          <div className={styles.enableRow}>
            <span className={styles.enableText}>
              {t('settings.accountAutomaticRepliesTab.automaticReplies')}
            </span>
            <Switch checked={isEnabled} onChange={handleSwitchChange} />
          </div>

          {isEnabled && (
            <div className={styles.manageRow}>
              <button className={styles.manageButton} onClick={() => handleManageClick(false)}>
                {t('settings.accountAutomaticRepliesTab.manage')}
              </button>
              <div className={styles.description}>{getDescription()}</div>
            </div>
          )}
        </div>
      </Spin>
    </div>
  )
}

export default AutomaticRepliesPanel
